import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "chiropractic-web-design-colorado",
  metaTitle: "Chiropractic Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom chiropractic web design across Colorado — fast, mobile-first, conversion-tested sites built to convert local searches into booked jobs. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Chiropractic marketing", to: "/chiropractic-marketing-colorado" },
  ],
  currentLabel: "Chiropractic Web Design Colorado",
  eyebrow: "Chiropractic Web Design — Colorado",
  h1: (
    <>
      Chiropractic <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded chiropractic websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Chiropractic websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first chiropractic sites with sticky click-to-call, dedicated service pages, and conversion-tested booking flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Chiropractic web design in Colorado",
    body: "Chiropractic web design is the practice of building conversion-focused, mobile-first websites for chiropractic companies — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with conversion-tested new-patient flows." },
    { title: "Online booking integration", body: "Direct integration with Jane, ChiroTouch, or your EHR for real-time booking." },
    { title: "Condition-based service pages", body: "Lower back, neck, sciatica, auto injury, sports injury, prenatal — each ranks independently." },
    { title: "New-patient special CTAs", body: "A/B-tested intro offers and exam packages with click-to-book CTAs." },
    { title: "Doctor bio + credentials", body: "Each DC gets a bio page with techniques, philosophy, and direct booking." },
    { title: "Schema and SEO at launch", body: "Chiropractor, MedicalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Patients search by pain, not by \"chiropractor\"", body: "We build pages for \"lower back pain\", \"sciatica relief\", \"whiplash treatment\" — the actual queries." },
    { heading: "Auto injury leads pay 5x", body: "Dedicated PI/MVA pages capture lawyer-referred patients with proper case-fee economics." },
    { heading: "Booking beats form-fill 3:1", body: "Real-time online booking converts 2–3x higher than \"request appointment\" forms." },
  ],
  relatedLinks: [
    { to: "/chiropractic-marketing-colorado", label: "Chiropractic marketing in Colorado — full hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado chiropractic case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a chiropractic website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a chiropractic website?", a: "Typical timeline is 4–6 weeks for a 12–20 page chiropractic site with full service and service-area pages. Larger multi-location chiropractic companies can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs chiropractic companies?", a: "No — we build chiropractic sites for companies across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Chiropractic Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for chiropractic companies across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/chiropractic-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
